/**
 * Devamsız vardiya karar modal'ı.
 *
 * BOSS 3 karardan birini seçer:
 *  - Onayla   → Vardiya 'devamsiz' kalır; no-show cezası mevcut maaş hesabında
 *               OTOMATİK uygulanıyor (10 saat × saatlik karşılık). Ek aksiyon yok.
 *  - İzin say → Vardiya 'izinli' yapılır; 1 günlük izin oluşturulur.
 *               Kaynak (yıllık/aylık/maas) seçilir.
 *  - Mazeret  → Vardiya 'iptal' yapılır; hiçbir kesinti olmaz.
 */

import { useState } from 'react'
import { X, AlertTriangle, Calendar, CheckCircle2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { devamsizKararVer, vardiyaSuresiDakika } from '@/lib/services/devamsiz-service'
import { KAYNAK_ETIKETLERI, type IzinKaynagi } from '@/lib/services/izin-service'
import type { Vardiya } from '@/lib/services/vardiya-service'
import type { KullaniciPersonel } from '@/types'

const STD_SAAT = 300
const GUN_SAAT = 10

type Karar = 'onaylandi' | 'izinSayildi' | 'mazeret'

interface Props {
  vardiya: Vardiya
  personel?: KullaniciPersonel
  acik: boolean
  onKapat: () => void
  onKaydedildi: () => void
}

export function DevamsizKararModal({ vardiya, personel, acik, onKapat, onKaydedildi }: Props) {
  const { firebaseUser } = useAuth()
  const [karar, setKarar] = useState<Karar>('onaylandi')
  const [izinKaynagi, setIzinKaynagi] = useState<IzinKaynagi>('aylik')
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  if (!acik) return null

  // No-show otomatik ceza tutarı (bilgi amaçlı — modal gösterimi için)
  const saatlikKarsi = personel
    ? personel.maasTipi === 'aylik'
      ? personel.ucret / STD_SAAT
      : personel.ucret
    : 0
  const otomatikCeza = Math.round(GUN_SAAT * saatlikKarsi)

  const vardiyaSureSt = (() => {
    const d = vardiyaSuresiDakika(vardiya)
    const s = Math.floor(d / 60)
    const k = d % 60
    return s > 0 ? `${s}s ${k}dk` : `${k}dk`
  })()

  const tarihEtiket = vardiya.tarih.split('-').reverse().join('.')

  async function kaydet() {
    if (!firebaseUser) {
      setHata('Oturum bulunamadı.')
      return
    }
    setKaydediliyor(true)
    setHata(null)
    try {
      await devamsizKararVer({
        vardiyaId: vardiya.id,
        karar,
        kararVerenId: firebaseUser.uid,
        izinKaynagi: karar === 'izinSayildi' ? izinKaynagi : undefined,
      })
      onKaydedildi()
      onKapat()
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi.')
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Devamsızlık Kararı
          </h2>
          <button
            onClick={onKapat}
            disabled={kaydediliyor}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Vardiya özeti */}
          <div className="bg-gray-50 rounded-xl p-3 text-sm">
            <p className="font-semibold text-gray-900">{personel?.isim ?? 'Personel'}</p>
            <p className="text-gray-600 mt-0.5">
              {tarihEtiket} · {vardiya.baslangic}–{vardiya.bitis} ({vardiyaSureSt})
            </p>
          </div>

          {/* Karar seçenekleri */}
          <div className="space-y-2">
            <KararSatiri
              secili={karar === 'onaylandi'}
              onSec={() => setKarar('onaylandi')}
              icerikRengi="bg-red-50 border-red-300"
              ikonRengi="text-red-600"
              ikon={<AlertTriangle className="w-4 h-4" />}
              baslik="Onayla (Devamsızlık)"
              aciklama={
                personel
                  ? `Sistem zaten otomatik 1 günlük mesai bedelini (${otomatikCeza} ₺) maaştan kesiyor. Ek aksiyon yok — sadece karar kaydı.`
                  : 'Sistem otomatik 1 günlük mesai bedelini maaştan kesiyor. Ek aksiyon yok.'
              }
            />

            <KararSatiri
              secili={karar === 'izinSayildi'}
              onSec={() => setKarar('izinSayildi')}
              icerikRengi="bg-green-50 border-green-300"
              ikonRengi="text-green-600"
              ikon={<Calendar className="w-4 h-4" />}
              baslik="İzin Say"
              aciklama="Vardiya 'izinli' olur, no-show kesintisi geri alınır. 1 günlük izin kaydedilir."
            />

            {karar === 'izinSayildi' && (
              <div className="ml-7 pl-3 border-l-2 border-green-200 space-y-1.5">
                {(['yillik', 'aylik', 'maas'] as IzinKaynagi[]).map((k) => (
                  <label
                    key={k}
                    className="flex items-center gap-2 text-sm cursor-pointer py-1"
                  >
                    <input
                      type="radio"
                      name="izinKaynagi"
                      checked={izinKaynagi === k}
                      onChange={() => setIzinKaynagi(k)}
                      className="text-green-600"
                    />
                    <span className="text-gray-700">{KAYNAK_ETIKETLERI[k]}</span>
                  </label>
                ))}
              </div>
            )}

            <KararSatiri
              secili={karar === 'mazeret'}
              onSec={() => setKarar('mazeret')}
              icerikRengi="bg-blue-50 border-blue-300"
              ikonRengi="text-blue-600"
              ikon={<CheckCircle2 className="w-4 h-4" />}
              baslik="Mazeret"
              aciklama="Vardiya 'iptal' olur, no-show kesintisi geri alınır. Hiçbir kesinti uygulanmaz."
            />
          </div>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
              {hata}
            </div>
          )}
        </div>

        <div className="flex gap-2 p-5 border-t border-gray-100">
          <button
            onClick={onKapat}
            disabled={kaydediliyor}
            className="flex-1 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl border border-gray-200 disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={kaydet}
            disabled={kaydediliyor}
            className="flex-1 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl disabled:opacity-50"
          >
            {kaydediliyor ? 'Kaydediliyor…' : 'Kararı Uygula'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface KararSatiriProps {
  secili: boolean
  onSec: () => void
  icerikRengi: string
  ikonRengi: string
  ikon: React.ReactNode
  baslik: string
  aciklama: string
}

function KararSatiri({ secili, onSec, icerikRengi, ikonRengi, ikon, baslik, aciklama }: KararSatiriProps) {
  return (
    <button
      onClick={onSec}
      type="button"
      className={`w-full text-left rounded-xl p-3 border-2 transition-colors ${
        secili ? icerikRengi : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 ${secili ? ikonRengi : 'text-gray-400'}`}>{ikon}</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{baslik}</p>
          <p className="text-xs text-gray-600 mt-0.5">{aciklama}</p>
        </div>
        <input
          type="radio"
          checked={secili}
          onChange={onSec}
          className="mt-1"
        />
      </div>
    </button>
  )
}
